
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Send, Trash2, Loader, CheckCircle } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

const VoiceRecorder = ({ customerId, leadId, onUploadSuccess }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    const mediaRecorderRef = useRef(null);
    const timerRef = useRef(null);
    const audioRef = useRef(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);

            const chunks = [];
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunks, { type: 'audio/mp4' }); // Chrome/FF usually record webm/mp4
                const url = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error("Error accessing microphone:", err);
            alert("Could not access microphone. Please allow permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const discardRecording = () => {
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
        setUploadSuccess(false);
    };

    const uploadRecording = async () => {
        if (!audioBlob) return;

        setIsUploading(true);
        const formData = new FormData();
        // Append ID based on what we have (customer or lead)
        if (customerId) formData.append('customer_id', customerId);
        if (leadId) formData.append('lead_id', leadId);

        // Append file with extension (browser MediaRecorder usually gives webm or mp4)
        formData.append('audio', audioBlob, 'feedback.mp4');

        try {
            // Do NOT set Content-Type manually — when posting FormData, axios (and the
            // browser) must auto-generate "multipart/form-data; boundary=----..." with
            // the correct boundary string. An explicit Content-Type header without the
            // boundary causes FastAPI to return 422 (unprocessable entity).
            const response = await axios.post(`${API_BASE_URL}/api/agents/feedback/voice`, formData, {
                withCredentials: true
            });

            if (response.data.status === 'success') {
                setUploadSuccess(true);
                // Pass full response data including extraction to parent
                if (onUploadSuccess) {
                    onUploadSuccess({
                        ...response.data,
                        customerId,
                        leadId
                    });
                }
                setTimeout(() => {
                    discardRecording(); // Reset after 2s success msg
                }, 2000);
            }
        } catch (error) {
            console.error("Upload failed", error);
            alert("Failed to upload voice note. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Render states
    if (uploadSuccess) {
        return (
            <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm animate-pulse">
                <CheckCircle className="w-5 h-5" />
                <span>Sent!</span>
            </div>
        );
    }

    if (isUploading) {
        return (
            <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing AI...</span>
            </div>
        );
    }

    if (audioBlob) {
        return (
            <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1 pl-3 pr-1">
                <button onClick={() => audioRef.current.play()} className="text-gray-700 hover:text-blue-600">
                    <Play className="w-4 h-4" />
                </button>
                <span className="text-xs font-mono text-gray-600">{formatTime(recordingTime)}</span>
                <audio ref={audioRef} src={audioUrl} className="hidden" />

                <div className="h-4 w-[1px] bg-gray-300 mx-1"></div>

                <button onClick={discardRecording} className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Discard">
                    <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={uploadRecording} className="p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-sm" title="Send Feedback">
                    <Send className="w-4 h-4" />
                </button>
            </div>
        );
    }

    if (isRecording) {
        return (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-full p-1 pl-3 pr-1 shadow-sm animate-pulse">
                <span className="text-xs font-mono text-red-600 font-bold">{formatTime(recordingTime)}</span>
                <button
                    onClick={stopRecording}
                    className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                >
                    <Square className="w-4 h-4 fill-current" />
                </button>
            </div>
        );
    }

    // Default state: Mic Button
    return (
        <button
            onClick={startRecording}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"
            title="Record Voice Note"
        >
            <Mic className="w-5 h-5" />
        </button>
    );
};

export default VoiceRecorder;
